import { Award, Shield, Star, CheckCircle2, FileCheck, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export const CertificationsCredentials = () => {
  const certifications = [
    {
      icon: Award,
      title: "GAF Master Elite",
      subtitle: "Top 3% Nationwide",
      description: "Only contractors who meet GAF's strict standards for licensing, insurance, reputation, and training earn this elite status.",
      benefits: [
        "Enhanced System Plus warranties available",
        "Priority support from GAF",
        "Extensive factory training completed",
        "Proven track record required"
      ],
      badge: "Elite Status"
    },
    {
      icon: Shield,
      title: "Licensed & Insured",
      subtitle: "Full Coverage Protection",
      description: "Complete licensing in Nassau & Suffolk counties with comprehensive insurance coverage.",
      benefits: [
        "Nassau County License #H18E0283300",
        "Suffolk County License #57322-H",
        "$2M General Liability Insurance",
        "Workers' Compensation Coverage"
      ],
      badge: "Fully Licensed"
    },
    {
      icon: Star,
      title: "Better Business Bureau",
      subtitle: "A+ Accredited Business",
      description: "Accredited with the BBB with an A+ rating, demonstrating commitment to customer satisfaction.",
      benefits: [
        "A+ Rating maintained since 2019",
        "Zero unresolved complaints",
        "Prompt dispute resolution",
        "Ethical business practices verified"
      ],
      badge: "A+ Rating"
    }
  ];

  const professionalMemberships = [
    {
      name: "National Roofing Contractors Association",
      abbr: "NRCA",
      description: "Professional standards and best practices"
    },
    {
      name: "Long Island Builders Institute",
      abbr: "LIBI",
      description: "Local industry leadership and networking"
    },
    {
      name: "Roofing Contractors Association of New York",
      abbr: "RCA-NY",
      description: "State-level professional development"
    }
  ];

  const badges = [
    { name: "GAF Master Elite", logo: "/assets/badges/gaf-master-elite-badge.png" },
    { name: "Owens Corning", logo: "/assets/badges/owens-corning-badge.png" },
    { name: "CertainTeed", logo: "/assets/badges/certainteed-badge.png" },
    { name: "IKO", logo: "/assets/badges/iko-badge.png" }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-background to-blue-50/50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Professional Credentials & Certifications
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Your peace of mind is backed by industry-leading certifications, comprehensive insurance, 
            and professional memberships that demonstrate our commitment to excellence.
          </p>
        </div>

        {/* Main Certifications */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {certifications.map((cert, index) => {
            const IconComponent = cert.icon;
            return (
              <Card key={index} className="border-2 hover:shadow-xl transition-all duration-300 group">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                    <IconComponent className="w-8 h-8 text-primary" />
                  </div>
                  <Badge variant="secondary" className="mx-auto bg-accent/10 text-accent mb-2">
                    {cert.badge}
                  </Badge>
                  <CardTitle className="text-xl text-primary">{cert.title}</CardTitle>
                  <p className="text-sm text-muted-foreground font-medium">{cert.subtitle}</p>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{cert.description}</p>
                  <div className="space-y-2">
                    {cert.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-accent mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Manufacturer Certifications */}
        <div className="mb-8 lg:mb-16">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-primary mb-6 lg:mb-8">
            Manufacturer Certifications
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-6 mb-6 lg:mb-8">
            {badges.map((badge, index) => (
              <Card key={index} className="text-center p-3 lg:p-6 group hover:shadow-lg transition-all duration-300">
                <img 
                  src={badge.logo} 
                  alt={`${badge.name} certification`} 
                  className="h-12 lg:h-16 w-auto mx-auto mb-2 lg:mb-4 group-hover:scale-105 transition-transform" 
                />
                <p className="text-xs lg:text-sm font-medium text-primary">{badge.name}</p>
                <p className="text-xs text-muted-foreground">Certified Contractor</p>
              </Card>
            ))}
          </div>
          <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
            <CardContent className="p-4 lg:p-6 text-center">
              <p className="text-muted-foreground text-sm lg:text-base leading-relaxed">
                Factory-trained and certified by all major roofing manufacturers, ensuring 
                proper installation techniques and warranty compliance for every project.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Professional Memberships */}
        <div className="mb-8 lg:mb-16">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-primary mb-6 lg:mb-8">
            Professional Memberships
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {professionalMemberships.map((membership, index) => (
              <Card key={index} className="text-center p-4 lg:p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 lg:mb-4">
                  <Users className="w-6 h-6 lg:w-8 lg:h-8 text-primary" />
                </div>
                <h4 className="font-bold text-primary mb-1 text-sm lg:text-base">{membership.abbr}</h4>
                <h5 className="text-xs lg:text-sm font-medium text-muted-foreground mb-2">{membership.name}</h5>
                <p className="text-xs text-muted-foreground leading-relaxed">{membership.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Insurance & Bonding Details */}
        <Card className="bg-gradient-to-r from-primary to-primary-dark text-primary-foreground">
          <CardContent className="p-6 lg:p-8">
            <div className="text-center mb-6 lg:mb-8">
              <h3 className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4">Complete Protection Coverage</h3>
              <p className="text-primary-foreground/90 text-sm lg:text-base leading-relaxed">
                Your investment is fully protected by comprehensive insurance and bonding
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              <div className="text-center">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-3 lg:mb-4">
                  <Shield className="w-6 h-6 lg:w-8 lg:h-8 text-primary-foreground" />
                </div>
                <h4 className="font-bold mb-2 text-sm lg:text-base">$2M Liability Insurance</h4>
                <p className="text-xs lg:text-sm text-primary-foreground/80 leading-relaxed">
                  Comprehensive general liability coverage protecting your property
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-3 lg:mb-4">
                  <FileCheck className="w-6 h-6 lg:w-8 lg:h-8 text-primary-foreground" />
                </div>
                <h4 className="font-bold mb-2 text-sm lg:text-base">Workers' Compensation</h4>
                <p className="text-xs lg:text-sm text-primary-foreground/80 leading-relaxed">
                  Full coverage protects you from any worker injury liability
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-3 lg:mb-4">
                  <CheckCircle2 className="w-6 h-6 lg:w-8 lg:h-8 text-primary-foreground" />
                </div>
                <h4 className="font-bold mb-2 text-sm lg:text-base">Performance Bond</h4>
                <p className="text-xs lg:text-sm text-primary-foreground/80 leading-relaxed">
                  Bonded contractor guaranteeing project completion
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};